import React from 'react'
import { Link } from 'react-router-dom'
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

export interface PageHeaderAction {
  label: string
  onClick: () => void
  variant?: 'primary' | 'secondary'
  Icon?: React.ElementType
}

export interface PageHeaderBreadcrumb {
  label: string
  path?: string
}

interface PageHeaderProps {
  title: string
  subtitle: string
  Icon: React.ElementType
  breadcrumbs: PageHeaderBreadcrumb[]
  actions?: PageHeaderAction[]
}

const PageHeader = ({ title, subtitle, Icon, breadcrumbs, actions = [] }: PageHeaderProps) => {
  const { t } = useTranslation()
  return (
    <div className="sticky top-0 z-20 bg-white">
      {/* Title section */}
      <div className="px-8 pt-6 pb-5 bg-white border-b border-gray-200">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Icon className="w-7 h-7 text-[#218cda] shrink-0" />
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-[#0a0d47] leading-tight">{title}</h1>
              <p className="mt-1 text-sm text-[#415055]">{subtitle}</p>
            </div>
          </div>
          {actions.length > 0 && (
            <div className="flex gap-2 shrink-0">
              {actions.map((action, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={action.onClick}
                  className={
                    action.variant === 'secondary'
                      ? 'flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 transition-colors'
                      : 'flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-[#218cda] text-white rounded-md hover:bg-[#1a7ac4] transition-colors'
                  }
                >
                  {action.Icon && <action.Icon className="w-4 h-4" />}
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Breadcrumb section */}
      <nav
        className="flex items-center gap-1.5 px-8 py-3 bg-white border-b border-gray-200 text-sm"
        aria-label="breadcrumb"
      >
        <Link to="/" className="flex items-center gap-1.5 text-[#218cda] hover:text-[#1a7ac4] transition-colors shrink-0">
          <HomeIcon className="w-4 h-4" />
          {t('NAVBAR.HOME')}
        </Link>
        {breadcrumbs.map((crumb, i) => (
          <React.Fragment key={i}>
            <ChevronRightIcon className="w-3 h-3 text-gray-400 shrink-0" />
            {crumb.path ? (
              <Link to={crumb.path} className="text-[#218cda] hover:text-[#1a7ac4] transition-colors">
                {crumb.label}
              </Link>
            ) : i === breadcrumbs.length - 1 ? (
              <span className="font-semibold text-[#0a0d47]">{crumb.label}</span>
            ) : (
              <span className="text-gray-500">{crumb.label}</span>
            )}
          </React.Fragment>
        ))}
      </nav>
    </div>
  )
}

export default PageHeader
